// Where the evolving weather reads the cloud reference. The globe and the
// rocketry orbital shell sample the full 8K reference at this address, so
// their clouds stay sharp and still register with the volumes, which read
// the same address through the lower-resolution weather field.
export const EARTH_WEATHER_UV_GLSL = /* glsl */`
float ewHash3(vec3 p){p=fract(p*.1031);p+=dot(p,p.yzx+33.33);return fract((p.x+p.y)*p.z);}
float ewNoise3(vec3 p){
 vec3 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
 return mix(mix(mix(ewHash3(i),ewHash3(i+vec3(1,0,0)),f.x),mix(ewHash3(i+vec3(0,1,0)),ewHash3(i+vec3(1,1,0)),f.x),f.y),
 mix(mix(ewHash3(i+vec3(0,0,1)),ewHash3(i+vec3(1,0,1)),f.x),mix(ewHash3(i+vec3(0,1,1)),ewHash3(i+vec3(1,1,1)),f.x),f.y),f.z);
}
// Gentle spherical deformation of the reference (a few kilometres).
vec3 earthWeatherWarp(vec3 n,float t){
 vec3 flow=vec3(t*.000035,0.,t*.000018);
 vec3 warp=vec3(ewNoise3(n*18.+flow),ewNoise3(n*18.+flow+17.),ewNoise3(n*18.+flow+41.))-.5;
 return normalize(n+warp*.0015);
}
// Latitude-dependent wind drift, in map U.
float earthWeatherDrift(float lat,float t){
 return mix(-.00000020,.00000055,smoothstep(.20,.60,abs(lat)))*t;
}
// Cloud reference coverage as the globe reads it: an unsharp mask while
// texels are smaller than a pixel, and a smooth cubic B-spline once they are
// magnified. Sharpening, or bilinear kinks under the steep cover curve,
// otherwise print the texel grid as blocks when looking down from low orbit.
float earthCloudCover(sampler2D tex,vec2 uv,vec2 texel,float sharpen){
 // Texels per pixel, the same along both screen axes (the square root of
 // the pixel's area in texels), so a tilted view does not flip the filter.
 vec2 ux=dFdx(uv)/texel,uy=dFdy(uv)/texel;
 float magnified=1.-smoothstep(.35,.9,sqrt(abs(ux.x*uy.y-ux.y*uy.x)));
 float c=texture2D(tex,uv).r;
 if(magnified<.999){
  float blur=(texture2D(tex,uv+vec2(texel.x,0.)).r+texture2D(tex,uv-vec2(texel.x,0.)).r+
   texture2D(tex,uv+vec2(0.,texel.y)).r+texture2D(tex,uv-vec2(0.,texel.y)).r)*.25;
  c=clamp(c+clamp(c-blur,-.1,.1)*sharpen*(1.-magnified),0.,1.);
 }
 if(magnified>.001){
  vec2 st=uv/texel-.5,i=floor(st),f=st-i;
  vec2 w0=(1.-f)*(1.-f)*(1.-f)/6.,w1=(4.-6.*f*f+3.*f*f*f)/6.,w2=(1.+3.*f+3.*f*f-3.*f*f*f)/6.,w3=f*f*f/6.;
  vec2 g0=w0+w1,g1=w2+w3;
  vec2 h0=(i-1.+w1/g0+.5)*texel,h1=(i+1.+w3/g1+.5)*texel;
  float cubic=g0.y*(g0.x*texture2D(tex,h0).r+g1.x*texture2D(tex,vec2(h1.x,h0.y)).r)+
   g1.y*(g0.x*texture2D(tex,vec2(h0.x,h1.y)).r+g1.x*texture2D(tex,h1).r);
  c=mix(c,cubic,magnified);
 }
 return c;
}
`;

// earthWeatherDrift for the CPU, in map U (lat in radians, t the same
// weatherTime): the streamed cloud pages are requested around the ground the
// drifted map lies over (src/rocketry/cloudimagery.js). Keep it in step with
// the GLSL above.
export function earthWeatherDriftCPU(lat, t) {
  const s = Math.min(Math.max((Math.abs(lat) - 0.20) / 0.40, 0), 1);
  return (-0.00000020 + 0.00000075 * s * s * (3 - 2 * s)) * t;
}

// Regional weather morphology on a sphere. Noise supplies internal cloud
// texture; it never supplies the outlines of the global weather systems.
export const EARTH_WEATHER_FRAGMENT = /* glsl */`
precision highp float;
${EARTH_WEATHER_UV_GLSL}
varying vec2 vUv;
uniform float uTime;
uniform sampler2D uOcean;
uniform float uOceanOn;
uniform sampler2D uGuide;
uniform float uGuideBlend;
const float PI=3.14159265359;
float hash3(vec3 p){p=fract(p*.1031);p+=dot(p,p.yzx+33.33);return fract((p.x+p.y)*p.z);}
float noise3(vec3 p){
 vec3 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
 return mix(mix(mix(hash3(i),hash3(i+vec3(1,0,0)),f.x),mix(hash3(i+vec3(0,1,0)),hash3(i+vec3(1,1,0)),f.x),f.y),
 mix(mix(hash3(i+vec3(0,0,1)),hash3(i+vec3(1,0,1)),f.x),mix(hash3(i+vec3(0,1,1)),hash3(i+vec3(1,1,1)),f.x),f.y),f.z);
}
float fbm(vec3 p){return noise3(p)*.57+noise3(p*2.01+13.7)*.28+noise3(p*4.03+7.9)*.15;}
vec3 sphere(float lon,float lat){return vec3(cos(lat)*cos(lon),sin(lat),-cos(lat)*sin(lon));}
vec2 localFrame(vec3 n,vec2 location){
 vec2 ll=location*PI/180.;
 vec3 c=sphere(ll.x,ll.y),e=vec3(-sin(ll.x),0.,-cos(ll.x)),up=cross(c,e);
 return vec2(atan(dot(n,e),dot(n,c)),asin(clamp(dot(n,up),-1.,1.)));
}
float region(vec3 n,vec2 location,vec2 extent){
 vec2 p=localFrame(n,location)/(extent*PI/180.);
 return exp(-dot(p,p)*1.4);
}
// Curl a finite frontal ribbon around a low-pressure centre. Its tail is
// narrow and its cloud shield broader. Neither is a closed noise contour.
float frontSystem(vec3 n,vec2 location,float bearing,float size){
 location.x+=uTime*.00022;
 vec2 p=localFrame(n,location)/size;
 if(dot(p,p)>12.)return 0.;
 float turn=bearing+sign(location.y)*(2.1+.85*sin(location.x*.09))*exp(-dot(p,p)*.65);
 vec2 q=mat2(cos(turn),-sin(turn),sin(turn),cos(turn))*p;
 float axis=q.y-.14*q.x-.10*sin(q.x*2.1);
 float width=mix(.15,.38,smoothstep(-2.,.5,q.x));
 float tail=exp(-pow(axis/width,2.))*smoothstep(-2.7,-1.8,q.x)*(1.-smoothstep(.35,1.1,q.x));
 vec2 h=(q-vec2(.05,.22))/vec2(.85,.48);
 float shield=exp(-dot(h,h)*1.7);
 return max(tail,shield*.88);
}
// A high-resolution cloud-density reference supplies the scale hierarchy
// that low-octave noise cannot reproduce: long fronts, fibrous cirrus and
// broken cellular decks. Advect it in latitude-dependent winds and deform
// it on the sphere; all renderers still consume the same evolving field.
vec4 guidedWeather(vec3 n,float lon,float lat){
 vec3 flow=vec3(uTime*.000035,0.,uTime*.000018);
 vec3 p=earthWeatherWarp(n,uTime);
 vec2 uv=vec2(atan(-p.z,p.x)/(2.*PI)+.5-earthWeatherDrift(lat,uTime),asin(clamp(p.y,-1.,1.))/PI+.5);
 float density=texture2D(uGuide,uv).r;
 float broad=textureLod(uGuide,uv,5.).r;
 float medium=textureLod(uGuide,uv,2.).r;
 // Vary optical depth gently inside an existing system, without adding
 // clouds to its clear gaps or converting grey margins into white paint.
 float evolution=.93+.14*noise3(n*45.+flow*.8);
 float opacity=clamp(density*evolution*1.08,0.,.98);
 float column=-log(max(1.-opacity,.02))/3.2;
 column+=.015*smoothstep(0.,.04,density);
 column=clamp(column,0.,1.);
 float tropics=1.-smoothstep(.20,.65,abs(lat));
 // Compact bright cells can convect outside the tropics too. A broad
 // smooth bank remains stratiform; latitude alone cannot pick the genus.
 float compact=smoothstep(.015,.18,medium-broad);
 float type=max(tropics*smoothstep(.18,.65,medium),compact*.85);
 float envelope=max(column,smoothstep(.025,.48,broad));
 return vec4(column,type,envelope,1.);
}
void main(){
 float lon=(vUv.x-.5)*2.*PI,lat=(vUv.y-.5)*PI;
 vec3 n=sphere(lon,lat);
 vec4 guide=vec4(0.);
 if(uGuideBlend>0.)guide=guidedWeather(n,lon,lat);
 if(uGuideBlend>=1.){gl_FragColor=guide;return;}
 float ocean=uOceanOn>.5?texture2D(uOcean,vUv,4.).r:1.;
 vec3 flow=vec3(uTime*.00010,0.,uTime*.000035);
 // Midlatitude storm tracks, with independent orientations and scales.
 // These seeded systems evolve slowly, rather than teleporting on LOD changes.
 float fronts=frontSystem(n,vec2(-42.,48.),-.6,.16);
 fronts=max(fronts,frontSystem(n,vec2(-157.,45.),.5,.19));
 fronts=max(fronts,frontSystem(n,vec2(23.,56.),-.85,.14)*.72);
 fronts=max(fronts,frontSystem(n,vec2(146.,43.),.1,.16)*.92);
 fronts=max(fronts,frontSystem(n,vec2(-76.,36.),-.8,.13)*.65);
 fronts=max(fronts,frontSystem(n,vec2(-115.,-48.),.7,.19));
 fronts=max(fronts,frontSystem(n,vec2(-24.,-53.),-.2,.22));
 fronts=max(fronts,frontSystem(n,vec2(70.,-47.),.4,.19));
 fronts=max(fronts,frontSystem(n,vec2(157.,-54.),-.5,.20)*.88);
 // The convergence zone meanders north of the equator. Longitudinal
 // moisture packets break it into squall complexes instead of a solid ring.
 float itczLatitude=.075+.07*sin(lon*2.+.7)+.04*sin(lon*5.-.9);
 float itczWidth=.045+.035*fbm(n*5.+23.);
 float tropical=exp(-pow((lat-itczLatitude)/itczWidth,2.));
 tropical*=smoothstep(.40,.63,fbm(n*7.+flow));
 // Humid continental convection (Amazon, equatorial Africa, maritime Asia).
 float humid=max(region(n,vec2(-62.,-4.),vec2(17.,9.)),region(n,vec2(23.,0.),vec2(15.,10.)));
 humid=max(humid,region(n,vec2(115.,-3.),vec2(25.,12.)));
 float convection=max(tropical,humid*.8);
 // Eastern-ocean stratocumulus: bounded decks over cool waters, rather
 // than cloud paint repeated across the desert belts and every continent.
 float marine=region(n,vec2(-91.,-19.),vec2(15.,11.));
 marine=max(marine,region(n,vec2(-6.,-20.),vec2(12.,12.)));
 marine=max(marine,region(n,vec2(-132.,26.),vec2(14.,10.))*.8);
 marine=max(marine,region(n,vec2(-28.,23.),vec2(13.,10.))*.7);
 marine=max(marine,region(n,vec2(99.,-28.),vec2(11.,9.))*.7);
 marine*=mix(.15,1.,ocean);
 // Broken trade-wind cumulus has a much lower column opacity than fronts.
 float tradeLatitude=exp(-pow((abs(lat)-.25)/.12,2.));
 float trades=tradeLatitude*ocean*smoothstep(.42,.66,fbm(n*9.+19.));
 // Lower, broader cloud fields travelling along the storm belts connect
 // frontal systems without manufacturing large closed contour rings.
 float stormBelt=exp(-pow((abs(lat)-.80)/.27,2.));
 float temperate=stormBelt*smoothstep(.27,.60,fbm(n*11.+flow*.3));
 float polar=smoothstep(1.02,1.35,abs(lat))*smoothstep(.28,.62,fbm(n*18.+41.))*.5;
 float cloudlets=fbm(n*100.+flow);
 float mesoscale=fbm(n*43.+vec3(7.,2.,9.)+flow*.4);
 float fine=noise3(n*360.+flow*2.);
 // Fibrous structure aligned with latitude/prevailing flow, mixed into
 // frontal shields. It remains internal to the weather envelope.
 float filaments=fbm(vec3(n.x*70.,n.y*270.,n.z*70.)+flow);
 float frontalShape=mix(.34,1.,smoothstep(.24,.70,cloudlets))*(.65+.35*filaments);
 float frontalCover=fronts*frontalShape;
 float marineCover=marine*mix(.16,.76,smoothstep(.25,.70,mesoscale))*(.70+.30*fine);
 // Convective towers cluster beneath spreading anvils. The wider shield
 // joins the cores locally without filling the entire tropical belt.
 float convectiveShape=max(smoothstep(.43,.70,cloudlets)*smoothstep(.30,.60,mesoscale),smoothstep(.54,.72,mesoscale)*.5);
 float convectiveCover=convection*convectiveShape;
 float tradeCover=trades*smoothstep(.49,.67,cloudlets)*.25;
 float temperateCover=max(temperate,polar)*mix(.08,.72,smoothstep(.27,.70,mesoscale))*(.55+.45*cloudlets);
 float coverage=max(max(frontalCover,marineCover),max(max(convectiveCover,tradeCover),temperateCover));
 float envelope=max(max(fronts,marine),max(max(convection,trades),max(temperate,polar)));
 float type=clamp((convectiveCover+tradeCover)/max(coverage,.001),0.,1.);
 // Keep the regional mask and the cloud type alongside the column field,
 // for the volume renderer. Thin cloud margins retain fractional opacity.
 gl_FragColor=mix(vec4(clamp(coverage,0.,1.),type,envelope,1.),guide,uGuideBlend);
}`;
